import { DEAL_LIST_PAGE_SIZE, parsePageParam } from "./dealListPagination";

export const DEAL_STATUS_FILTERS = [
  { value: "", label: "Усі" },
  { value: "pending", label: "Очікує" },
  { value: "in_progress", label: "В процесі" },
  { value: "closed", label: "Закриті" },
  { value: "cancelled", label: "Скасовані" },
];

export const DEAL_ROLE_FILTERS = [
  { value: "", label: "Усі" },
  { value: "seller", label: "Я продавець" },
  { value: "buyer", label: "Я покупець" },
];

export const DEAL_COMPENSATION_FILTERS = [
  { value: "", label: "Усі" },
  { value: "configured", label: "Є" },
  { value: "none", label: "Немає" },
  { value: "percent", label: "Відсоток" },
  { value: "fixed", label: "Фіксована" },
];

export const DEAL_COMPENSATION_CURRENCY_FILTERS = [
  { value: "", label: "Усі валюти" },
  { value: "UAH", label: "UAH" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
];

export const DEAL_ORDERING_OPTIONS = [
  { value: "-created_at", label: "Спочатку нові" },
  { value: "created_at", label: "Спочатку старі" },
  { value: "-updated_at", label: "Нещодавно оновлені" },
  { value: "price", label: "Ціна ↑" },
  { value: "-price", label: "Ціна ↓" },
];

const ALLOWED_STATUSES = new Set(
  DEAL_STATUS_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_ROLES = new Set(
  DEAL_ROLE_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_COMPENSATIONS = new Set(
  DEAL_COMPENSATION_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_COMPENSATION_CURRENCIES = new Set(
  DEAL_COMPENSATION_CURRENCY_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_ORDERINGS = new Set(
  DEAL_ORDERING_OPTIONS.map((item) => item.value),
);

const CANONICAL_QUERY_KEYS = new Set([
  "q",
  "status",
  "role",
  "compensation",
  "compensation_currency",
  "ordering",
  "page",
]);
const MAX_Q_LENGTH = 100;

/**
 * Canonical Deal list URL → API query contract.
 * page=1 is omitted from the URL; default ordering is server `-created_at`.
 * Applied search is URL/API key `q` (trimmed; draft stays local until submit).
 * Shared partner compensation filters use `compensation` / `compensation_currency`.
 */
export function normalizeDealListSearchQuery(raw) {
  if (raw == null) return "";
  const text = String(raw).trim();
  if (!text) return "";
  return text.slice(0, MAX_Q_LENGTH);
}

export function readDealListQueryParams(search) {
  const params = new URLSearchParams(
    typeof search === "string"
      ? search
      : search && typeof search.toString === "function"
        ? search.toString()
        : "",
  );
  const rawStatus = params.get("status") || "";
  const rawRole = params.get("role") || "";
  const rawCompensation = params.get("compensation") || "";
  const rawCurrency = params.get("compensation_currency") || "";
  const rawOrdering = params.get("ordering") || "";
  const ordering =
    ALLOWED_ORDERINGS.has(rawOrdering) && rawOrdering !== "-created_at"
      ? rawOrdering
      : "";
  const compensation = ALLOWED_COMPENSATIONS.has(rawCompensation)
    ? rawCompensation
    : "";
  // none + currency is contradictory — drop currency during canonicalization.
  let compensationCurrency = ALLOWED_COMPENSATION_CURRENCIES.has(rawCurrency)
    ? rawCurrency
    : "";
  if (compensation === "none") {
    compensationCurrency = "";
  }
  return {
    page: parsePageParam(params.get("page")),
    q: normalizeDealListSearchQuery(params.get("q")),
    status: ALLOWED_STATUSES.has(rawStatus) ? rawStatus : "",
    role: ALLOWED_ROLES.has(rawRole) ? rawRole : "",
    compensation,
    compensation_currency: compensationCurrency,
    ordering,
  };
}

export function buildDealListSearchParams({
  page = 1,
  q = "",
  status = "",
  role = "",
  compensation = "",
  compensation_currency = "",
  ordering = "",
} = {}) {
  const next = new URLSearchParams();
  const normalizedQ = normalizeDealListSearchQuery(q);
  if (normalizedQ) {
    next.set("q", normalizedQ);
  }
  if (status && ALLOWED_STATUSES.has(status)) {
    next.set("status", status);
  }
  if (role && ALLOWED_ROLES.has(role)) {
    next.set("role", role);
  }
  const safeCompensation =
    compensation && ALLOWED_COMPENSATIONS.has(compensation) ? compensation : "";
  if (safeCompensation) {
    next.set("compensation", safeCompensation);
  }
  // Currency is omitted when compensation=none (contradictory combination).
  if (
    compensation_currency
    && ALLOWED_COMPENSATION_CURRENCIES.has(compensation_currency)
    && safeCompensation !== "none"
  ) {
    next.set("compensation_currency", compensation_currency);
  }
  if (ordering && ALLOWED_ORDERINGS.has(ordering) && ordering !== "-created_at") {
    next.set("ordering", ordering);
  }
  if (page > 1) {
    next.set("page", String(page));
  }
  return next;
}

/**
 * True when the raw browser search string differs from the C2 canonical form.
 * Unknown keys, page=1, invalid tokens, and default ordering must be rewritten.
 */
export function dealListSearchNeedsCanonicalization(search) {
  const raw = new URLSearchParams(
    typeof search === "string"
      ? search
      : search && typeof search.toString === "function"
        ? search.toString()
        : "",
  );
  const canonical = buildDealListSearchParams(readDealListQueryParams(raw));

  const rawKeys = [...raw.keys()].sort();
  for (const key of rawKeys) {
    if (!CANONICAL_QUERY_KEYS.has(key)) {
      return true;
    }
  }

  return raw.toString() !== canonical.toString();
}

export function buildDealListFetchParams({
  page = 1,
  q = "",
  status = "",
  role = "",
  compensation = "",
  compensation_currency = "",
  ordering = "",
} = {}) {
  const params = {
    page,
    page_size: DEAL_LIST_PAGE_SIZE,
  };
  const normalizedQ = normalizeDealListSearchQuery(q);
  if (normalizedQ) {
    params.q = normalizedQ;
  }
  if (status && ALLOWED_STATUSES.has(status)) {
    params.status = status;
  }
  if (role && ALLOWED_ROLES.has(role)) {
    params.role = role;
  }
  const safeCompensation =
    compensation && ALLOWED_COMPENSATIONS.has(compensation) ? compensation : "";
  if (safeCompensation) {
    params.compensation = safeCompensation;
  }
  if (
    compensation_currency
    && ALLOWED_COMPENSATION_CURRENCIES.has(compensation_currency)
    && safeCompensation !== "none"
  ) {
    params.compensation_currency = compensation_currency;
  }
  if (ordering && ALLOWED_ORDERINGS.has(ordering)) {
    params.ordering = ordering;
  }
  return params;
}

export function dealListHasActiveFilters({
  q = "",
  status = "",
  role = "",
  compensation = "",
  compensation_currency = "",
} = {}) {
  return Boolean(
    normalizeDealListSearchQuery(q)
    || status
    || role
    || compensation
    || compensation_currency,
  );
}
